import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Heart, Copy, Check, Loader2 } from "lucide-react";

export function InvitePartner() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const add = useMutation({
    mutationFn: async () => (await api.post("/buyer/cobuyers", form)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["workspace"] });
      setResult(d);
      setForm({ name: "", email: "" });
      toast.success("Partner invited" + (d.email_sent ? " — email sent" : ""));
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const copy = () => { navigator.clipboard.writeText(result.invite_link); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <Card className="p-6 rounded-2xl" data-testid="invite-partner-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Buying together?</p>
      <p className="text-sm text-muted-foreground mb-4">Invite your partner to share this workspace — same homes, checklist and comparisons.</p>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full rounded-full gap-2" data-testid="invite-partner-btn"><Heart className="h-4 w-4" /> Invite my partner</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Invite your partner</DialogTitle>
            <DialogDescription>They'll get their own sign-in and share everything in your workspace.</DialogDescription>
          </DialogHeader>
          {!result ? (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="partner-email-input" /></div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm">Invite sent to <strong>{result.name}</strong>. You can share this link too:</p>
              <div className="flex gap-2"><Input readOnly value={result.invite_link} className="text-xs" data-testid="partner-invite-link" /><Button variant="outline" onClick={copy}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button></div>
            </div>
          )}
          <DialogFooter>
            {!result ? (
              <Button onClick={() => add.mutate()} disabled={!form.name || !form.email || add.isPending} data-testid="partner-send-btn" className="rounded-full">
                {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send invite"}
              </Button>
            ) : (
              <Button onClick={() => { setOpen(false); setResult(null); }} className="rounded-full">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
